<template>
    <!-- Conteneur principal pleine largeur -->
    <div class="ppv2-w-full ppv2-relative">
        <!-- Loading Overlay -->
        <div
            v-if="loading"
            class="ppv2-fixed ppv2-inset-0 ppv2-z-50 ppv2-bg-opacity-90 ppv2-flex ppv2-items-center ppv2-justify-center"
        >
            <div class="ppv2-text-center">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-16 ppv2-w-16 ppv2-border-b-4 ppv2-border-purple-600 ppv2-mx-auto"
                ></div>
                <span class="ppv2-mt-4 ppv2-block ppv2-text-lg poppinsMedium ppv2-text-gray-700">{{
                    $t('invoices.loading')
                }}</span>
            </div>
        </div>
        <div v-else>
            <div class="ppv2-rounded-lg ppv2-shadow-sm ppv2-p-4 md:ppv2-p-6">
                <!-- Message d'erreur -->
                <div v-if="error" class="ppv2-bg-red-50 ppv2-border ppv2-border-red-200 ppv2-rounded-lg ppv2-p-4 ppv2-mb-4">
                    <div class="ppv2-flex ppv2-items-center">
                        <svg class="ppv2-h-5 ppv2-w-5 ppv2-text-red-600 ppv2-mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <span class="ppv2-text-red-800">{{ error }}</span>
                    </div>
                </div>

                <!-- Header -->
                <Header :title="$t('invoices.orders')" />

                <!-- Stats Card -->
                <div class="ppv2-max-w-1/2 ppv2-mb-6 ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-8">
                    <div class="cardCAFact ppv2-flex align-items-center ppv2-gap-1">
                        <div class="ppv2-px-4 ppv2-py-6 ppv2-text-white ppv2-w-[49%]">
                            <div :style="{ fontSize: normalizeFontSize(20) }">{{ $t('invoices.orders_count') }}</div>
                            <div class="poppinsSemiBold" :style="{ fontSize: normalizeFontSize(25) }">
                                {{ stats.draft_orders?.count || 0 }}
                            </div>
                        </div>
                        <div class="ppv2-w-[1px] ppv2-h-[90%] ppv2-bg-white" />

                        <div class="ppv2-px-4 ppv2-py-6 ppv2-text-white ppv2-w-[49%]">
                            <div :style="{ fontSize: normalizeFontSize(20) }">{{ $t('invoices.total_ttc') }}</div>
                            <div class="poppinsSemiBold" :style="{ fontSize: normalizeFontSize(25) }">
                                {{ formatCurrency(stats.draft_orders?.amount || 0) }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Filters Row -->
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row ppv2-justify-start md:ppv2-justify-between">
                    <div class="ppv2-mb-6 ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-gap-3">
                        <Search
                            v-model="searchQuery"
                            :emitGlobal="false"
                            :keep-open-on-search="true"
                            @search-updated="handleSearchUpdate"
                        />
                        <!-- Date Range Picker -->
                        <invoice-date-filter
                            :start-date.sync="dateRange.start"
                            :end-date.sync="dateRange.end"
                            @change="handleDateChange"
                        >
                        </invoice-date-filter>

                        <!-- More Filters (Visual Match V3) -->
                        <filter-dropdown
                            :label="$t('invoices.filter_status')"
                            :options="availableFilters.paymentStatuses || []"
                            v-model="filters.payment_status"
                            :multiple="true"
                            @change="handleFilterChange"
                        >
                            <template #icon>
                                <FileText :size="17" />
                            </template>
                        </filter-dropdown>

                        <!-- <filter-dropdown
                            :label="$t('invoices.type')"
                            :options="availableFilters.types || []"
                            v-model="filters.type_id"
                            :multiple="true"
                            @change="handleFilterChange"
                        >
                            <template #icon>
                                <CalendarClock :size="18" />
                            </template>
                        </filter-dropdown> -->

                        <!-- Filtres -->
                        <button
                            @click="showAdvancedFilters = true"
                            class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-text-gray-700 hover:ppv2-bg-gray-50 ppv2-transition-colors"
                            :style="{ fontSize: normalizeFontSize(14) }"
                        >
                            <LayoutGrid :size="18" />
                            <span class="ppv2-hidden md:ppv2-inline">{{ $t('invoices.filter_advanced') }}</span>
                        </button>
                        <div v-if="canExportOrders" class="ppv2-relative md:ppv2-hidden ppv2-block">
                            <ExportSelect
                                :label="$t('invoices.export')"
                                :options="exportOptions"
                                @export-selected="handleExport"
                                :hide-label-on-mobile="true"
                            />
                        </div>
                    </div>

                    <!-- Export Button (Float Right) -->
                    <div v-if="canExportOrders" class="ppv2-relative ppv2-hidden md:ppv2-block">
                        <ExportSelect :label="$t('invoices.export')" :options="exportOptions" @export-selected="handleExport" />
                    </div>
                </div>

                <!-- Draft Orders DataTable -->
                <div>
                    <draft-orders-data-table
                        ref="draftsDataTable"
                        :start-date="dateRange.start"
                        :end-date="dateRange.end"
                        :search-query="searchQuery"
                        :filters="filters"
                        :can-modify-order="canModifyOrder"
                        :can-delete-order="canDeleteOrder"
                        @stats-updated="handleStatsUpdate"
                    ></draft-orders-data-table>
                </div>
            </div>
        </div>

        <!-- Advanced Filters Modal -->
        <invoice-advanced-filters
            v-if="showAdvancedFilters"
            :available-filters="availableFilters"
            :current-filters="filters"
            @close="showAdvancedFilters = false"
            @update="handleAdvancedFiltersUpdate"
            save-url="/back-office/invoices-v3/save-filters?type=invoices_v3_drafts"
        />
    </div>
</template>

<script>
import Search from '../V3/Experience/components/Search.vue'
import ExportSelect from '../Shared/ExportSelect.vue'
import Header from '../Shared/Header.vue'
import normalizeSize from '../../utils/normalizeSize'
import { FileText, ChevronDown, CalendarClock, LayoutGrid } from 'lucide-vue'
import FilterDropdown from '../Invoices/FilterDropdown.vue'
import InvoiceAdvancedFilters from '../Invoices/InvoiceAdvancedFilters.vue'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'InvoicesEnAttentePage',
    components: {
        Search,
        ExportSelect,
        Header,
        FileText,
        ChevronDown,
        CalendarClock,
        LayoutGrid,
        FilterDropdown,
        InvoiceAdvancedFilters,
    },
    props: {
        canExportOrders: {
            type: Boolean,
            default: false,
        },
        canModifyOrder: {
            type: Boolean,
            default: false,
        },
        canDeleteOrder: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            error: null,
            searchQuery: '',
            showAdvancedFilters: false,
            dateRange: {
                start: new Date().toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0],
            },
            stats: {
                draft_orders: {
                    count: 0,
                    amount: 0,
                },
            },
            draftsMeta: { total: 0 },
            exportOptions: [
                { value: 'csv', label: this.$t('invoices.export_csv') },
                { value: 'xls', label: this.$t('invoices.export_excel') },
            ],
            // New Filter Data
            filters: {
                payment_status: [],
                status_id: [],
                type_id: [],
                has_refund: null,
                voucher_id: null,
                promo_code: null,
                payment_method_id: null,
                prestations: [],
                products: [],
                packages: [],
                series: [],
                staff_id: null,
                total_greater_than: null,
                total_lesser_than: null,
            },
            availableFilters: {
                paymentStatuses: [
                    { id: 'cancelled', label: this.$t('invoices.payment_status.cancelled') },
                    { id: 'pending', label: this.$t('invoices.payment_status.pending') },
                    { id: 'partially_paid', label: this.$t('invoices.payment_status.partially_paid') },
                    { id: 'paid', label: this.$t('invoices.payment_status.paid') },
                ],
                orderStatuses: [],
                types: [],
                staffs: [],
                paymentMethods: [],
                prestations: [],
                products: [],
                packages: [],
                series: [],
                vouchers: [],
                promotions: [],
            },
        }
    },
    mounted() {
        this.loadAvailableFilters()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        handleStatsUpdate(stats) {
            this.stats.draft_orders = {
                count: stats.count || 0,
                amount: stats.amount || 0,
            }
            this.draftsMeta.total = stats.count || 0
        },
        async loadAvailableFilters() {
            try {
                // Using invoice filters as base, but draft orders might care about some different things
                // For simplified UX, reusing same endpoint logic
                const response = await this.axios.get('/back-office/invoices-v3/filter-data', {
                    params: { type: 'invoices_v3_drafts' },
                })
                const data = response.data.data || response.data // Handle Resource wrapper

                if (data) {
                    this.availableFilters = {
                        ...this.availableFilters,
                        ...data,
                        // Map invoiceTypes to types if needed, or use the ones from data
                        orderStatuses: data.orderStatuses || [],
                        staffs: data.staffs || [],
                        paymentMethods: data.paymentMethods || [],
                        prestations: data.prestations || [],
                        products: data.products || [],
                        packages: data.packages || [],
                        series: data.series || [],
                        vouchers: data.vouchers || [],
                        promotions: data.promotions || [],
                    }

                    if (!this.availableFilters.types || this.availableFilters.types.length === 0) {
                        this.availableFilters.types = [
                            { id: 'Anniversaire', label: this.$t('invoices.types.anniversaire') },
                            { id: 'Entreprise', label: this.$t('invoices.types.entreprise') },
                            { id: 'EVG/EVJF', label: this.$t('invoices.types.evg_evjf') },
                            { id: 'Manuel', label: this.$t('invoices.types.manuel') },
                        ]
                    }
                    // Ensure statuses have label for consistent usage if they only come as name
                    if (this.availableFilters.orderStatuses) {
                        this.availableFilters.orderStatuses = this.availableFilters.orderStatuses.map((s) => ({
                            ...s,
                            label: s.name, // Component expects 'label'
                            id: s.id,
                        }))
                    }

                    // Load saved user filters if available
                    if (data.userFilters && data.userFilters.filters) {
                        this.filters = { ...this.filters, ...data.userFilters.filters }
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        handleAdvancedFiltersUpdate(newFilters) {
            this.filters = { ...this.filters, ...newFilters }
            this.handleFilterChange()
        },
        handleFilterChange() {
            // Stats will be updated automatically via @stats-updated event from DraftOrdersDataTable
        },
        handleDateChange() {
            this.handleFilterChange()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.$nextTick(() => {
                if (this.$refs.draftsDataTable) {
                    this.$refs.draftsDataTable.handleSearchUpdate(searchValue)
                }
            })
        },
        async handleExport(format) {
            console.log(`📤 Export ${format} demandé`)
            if (this.$refs.draftsDataTable) {
                await this.$refs.draftsDataTable.exportData(format)
            }
        },
        formatCurrency(amount) {
            const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: this.$codeCurrency,
            }).format(amount || 0)
        },
    },
}
</script>

<style scoped lang="scss">
.menuDropdown {
    @apply ppv2-bg-gray-900 ppv2-bg-opacity-80 ppv2-text-gray-400;
    margin-top: 10px;
    z-index: 9;
}

.dropdownItem:hover {
    @apply ppv2-bg-indigo-900 ppv2-text-white;
}

.dropdownItem:hover * {
    @apply ppv2-text-white;
}

.dark {
    .menuDropdown {
        @apply ppv2-bg-gray-800 ppv2-bg-opacity-90;
    }
}

.cardCAFact {
    background-image: url('/media/bo/backgounds/bgCardFacture.png');
    background-size: cover;
    background-position: center;
    box-shadow: -1px 1px 6.7px 0px #00000040;
    border-radius: 15px;
}
</style>
